/**
 * Come si legge lo stato di un incontro Limulo, senza affermare cio' che il server non dice.
 *
 * ⚠️ Le tre trappole che questo file esiste per chiudere, tutte dalla forma di `LimuloMatch`
 * (`types/limulo.ts`, derivata da `sanitizeMatch` — ADR-0020/0049):
 *
 *  1. **Un bye NON e' un incontro incompleto.** `isBye` e uno slot giocatore a `null`
 *     convivono: `players.b === null` e' la NORMA su un bye, ed e' un'ANOMALIA altrove.
 *     Chi guarda solo gli slot mostra «in attesa di avversario» su un turno gia' risolto;
 *     chi guarda solo `isBye` mostra un incontro vuoto su un tabellone non ancora composto.
 *     Vanno letti nell'ordine, e `isBye` viene prima.
 *  2. **`result` presente non significa «c'e' un vincitore».** `winnerPlayerId` puo' essere
 *     `null` su un referto depositato — pareggio, referto in contestazione, o semplicemente
 *     un dato che la facciata non porta. Dedurre il vincitore dal punteggio sarebbe
 *     inventare una regola di gioco che non e' nostra (ogni gioco ha la sua).
 *  3. 🔴 **`winnerPlayerId` e' l'id di una PARTE, non di un utente** (ADR-0017: il `matchId`
 *     deriva dalle parti). Confrontarlo con un id utente non da' un errore: **non combacia
 *     mai**, e il vincitore sparisce in silenzio da ogni incontro. Qui si confronta solo con
 *     `playerA`/`playerB`, che sono anch'essi id di parte.
 */

import type { LimuloMatch } from '../../types/limulo.ts';

/**
 * Lo stato di un incontro, per come si puo' onestamente dedurlo dalla facciata.
 *
 * `reported` esiste apposta ed e' la voce che non si vorrebbe scrivere: un referto c'e', ma
 * **chi ha vinto non e' dichiarato**. Collassarlo su `decided` farebbe mostrare un vincitore
 * inventato; collassarlo su `pending` nasconderebbe un referto che esiste.
 *
 * 🔴 `disputed` **non e' una sfumatura di `reported`**, ed e' il motivo per cui la voce
 * esiste: `reported` dice «non sappiamo chi ha vinto», `disputed` dice «i due non sono
 * d'accordo, l'incontro e' fermo, e decide lo staff». Il primo e' l'assenza di un dato, il
 * secondo e' un dato — e per un anno di schermate sono stati la stessa cosa.
 */
export type LimuloMatchState = (typeof LIMULO_MATCH_STATES)[number];

/**
 * 🔑 L'elenco degli stati come VALORE, e il tipo sopra ne discende: **non esiste piu' un modo di
 * aggiungere uno stato senza passare da qui**. Prima il tipo era un'unione a mano, invisibile a
 * runtime, e la voce e' entrata nella LOGICA (`proposed`, `disputed`) senza entrare nel
 * DIZIONARIO: lo schermo ha stampato `MATCHES.STATE.DISPUTED`. Ora `tools/verify-limulo.ts`
 * legge questa lista e pretende `matches.state.<stato>` in it e in en — il prossimo stato
 * nuovo rompe il cancello, non lo schermo.
 */
export const LIMULO_MATCH_STATES = [
  'bye',
  'incomplete',
  'pending',
  'proposed',
  'disputed',
  'reported',
  'decided',
] as const;

/**
 * Il `resultType` di un referto **proposto e non ancora confermato**.
 *
 * ⚠️ Non e' una scelta nostra: e' la forma decisa da chi possiede il motore
 * `[T @gionko, kb#146, 2026-09-08T15:29:10Z]` — `status: 'pending'` sul match e il referto
 * dentro `result` con questo `resultType`. La ragione tecnica e' che il sanitizer del BFF
 * (`policy.js`) **scarta ogni chiave nuova a livello match**, mentre `resultType` e `source`
 * sono stringhe libere: un campo `proposedResult` non arriverebbe mai al client.
 */
export const LIMULO_RESULT_PROPOSED = 'proposed';

/**
 * Il `resultType` di un referto **CONTESTATO**: i due giocatori hanno mandato punteggi
 * diversi, e la decisione e' passata allo staff.
 *
 * 🔴 **E' il terzo valore, ed e' quello che questo file esisteva per NON gestire.** Fino a
 * `main@1c7665e` `matchStateOf` confrontava solo `'proposed'`: una disputa cadeva sul ramo
 * successivo e usciva come `reported` — «referto senza vincitore». Nessun errore, nessun
 * rosso: *la schermata taceva sul fatto che l'incontro fosse fermo e che a deciderlo sarebbe
 * stato lo staff.* E' la classe peggiore di difetto che questa sezione possa produrre, la
 * stessa che il ramo `proposed` era stato scritto per chiudere.
 *
 * ⚠️ Il match resta `status: 'pending'` lato server e `result.scoreA/scoreB` sono quelli
 * **della proposta**, non del contestatore: i numeri di chi ha contestato stanno in
 * `disputedScoreA/B`, che il sanitizer del BFF **scarta** — e per questo viaggiano anche in
 * `result.reason` (v. `disputedScoreOf`). `[T @gionko, kb#146 §1/§5, 2026-09-09T23:04:25Z]`
 */
export const LIMULO_RESULT_DISPUTED = 'disputed';

/**
 * I DUE modi in cui un risultato puo' entrare, e il vocabolario e' **chiuso**
 * `[T @gionko, kb#146, 2026-09-10T00:15:25Z]`: `'manual'` (refertano i giocatori con la conferma,
 * o lo staff) e `'provider'` (lo manda il gioco — oggi Clash Royale via `app-games`).
 * Precedenza: **override sull'incontro > torneo > default `manual`**, applicata dal motore.
 *
 * ⚠️ **Il default NON si applica qui.** Un campo vuoto significa «il server non l'ha detto», e
 * riempirlo con `manual` vorrebbe dire accendere il bottone del referto su un incontro di cui non
 * sappiamo il modo. La costante che serve al client e' quella che **spegne**, e si confronta per
 * uguaglianza: v. il ramo `provider-result` in `matchActions.ts`.
 */
export const LIMULO_RESULT_MODE_PROVIDER = 'provider';
export const LIMULO_RESULT_MODE_MANUAL = 'manual';

/**
 * Cosa DIRE all'utente del `resultType` di un referto — e la risposta di default e' niente.
 *
 * Fino al 2026-09-23 il dettaglio stampava il valore grezzo («Tipo: normal», «Tipo: forfeit»):
 * vocabolario del motore, non una frase. Il motore nativo ne manda cinque
 * [M `qlash-limulo@origin/staging`, `app-qtournaments/app/superficie/capability/incontri.js`
 * `rigaDiIncontro` e `:263`]: `normal`, `forfeit` (dalla 1.65.0 anche un `contest` marcato
 * `walkover`), `bye`, `proposed`, `disputed`.
 * ⇒ Si nominano solo le DEVIAZIONI che nessun'altra parte della schermata racconta: `forfeit` e
 * `bye`. `normal` e' il caso pulito (dirlo e' rumore); `proposed` e `disputed` hanno gia' il loro
 * pannello (`matchStateOf`). Un valore ignoto tace a schermo: tradurlo vorrebbe dire inventare
 * una corrispondenza che nessuno ha promesso.
 * [decisione `tma-orc` su delega I-124 di Luca, 2026-09-23 — il dossier la lasciava aperta come
 * «la domanda vera e' se quel campo vada mostrato all'utente».]
 */
export type LimuloResultKindLabel = 'forfeit' | 'bye';
export function resultKindLabelOf(resultType: string | null | undefined): LimuloResultKindLabel | null {
  if (resultType === 'forfeit' || resultType === 'bye') return resultType;
  return null;
}

/**
 * Quante gare della serie DIRE vinte a tavolino (kb#298) — e la risposta di default e' niente.
 *
 * Il motore conta le gare con `walkover` (qTournaments 1.80.0, `gareVinteATavolino`) e scrive `0` su un
 * incontro secco o giocato; il BFF lo porta sulla riga e sulla busta della LETTURA del referto
 * (qlash-limulo#169). Le conta anche su una serie IN CORSO.
 * ⇒ Si dice solo un intero POSITIVO: lo zero e' il caso pulito (dirlo e' rumore, come `normal` sopra),
 * `null` e' «il bordo non lo dice» e non si scrive nemmeno lui.
 * ⚠️ `primario`, quando parla, vince su `ripiego` — anche col suo zero. QUALE lettura e' primaria lo
 * decide chi ne ha due: la scheda passa il referto solo quando e' lui a dare il punteggio mostrato.
 */
export function walkoverGamesOf(
  primario: number | null | undefined,
  ripiego?: number | null | undefined,
): number | null {
  const n = primario ?? ripiego ?? null;
  return n !== null && Number.isSafeInteger(n) && n > 0 ? n : null;
}

/**
 * La finestra del silenzio-assenso: 15 minuti dal referto proposto.
 *
 * 🔴 **E' un numero del SERVER tenuto qui a mano, e va saputo.** Il contratto porta un
 * `expiresAt`, ma il sanitizer del BFF lo scarta insieme a ogni altra chiave nuova a livello
 * match `[T @gionko, kb#146 §2]`: al client non arriva. L'unica ancora che abbiamo e'
 * `result.recordedAt`, che invece passa `[M 2026-09-09 23:2xZ, staging: il referto di Ciao
 * Cup r1 esce con `recordedAt` valorizzato]`.
 *
 * ⛔ **Quindi questa costante governa SOLO LA COPY, mai un'azione.** Se il server cambiasse
 * la finestra senza dirlo, il peggio che puo' succedere e' una frase in anticipo o in
 * ritardo di qualche minuto; un bottone acceso o spento su questo numero sarebbe invece una
 * decisione presa al posto del server, con lo scarto d'orologio del telefono dentro.
 * ⇒ i permessi restano quelli che la facciata dichiara (`canSubmitResult`).
 */
export const LIMULO_PROPOSAL_WINDOW_MS = 15 * 60 * 1000;

/** `'a'` o `'b'` solo se il vincitore dichiarato e' UNA delle due parti di questo incontro. */
export function winnerSideOf(match: LimuloMatch): 'a' | 'b' | null {
  const winner = match.winnerPlayerId;
  if (!winner) return null;
  // ⚠️ Nessun fallback «se non e' A allora e' B». Un `winnerPlayerId` che non combacia con
  // nessuna delle due parti e' un dato che non capiamo, e la risposta onesta e' «non lo so»:
  // dedurne il lato darebbe un vincitore certo a partire da un'incoerenza.
  if (winner === match.playerA) return 'a';
  if (winner === match.playerB) return 'b';
  return null;
}

/**
 * Lo stato dell'incontro. L'ordine dei rami E' la logica, e non si riordina:
 * `isBye` prima degli slot, gli slot prima del referto.
 */
export function matchStateOf(match: LimuloMatch): LimuloMatchState {
  // 1. Il bye vince su tutto: e' un turno passato, non un incontro da giocare.
  if (match.isBye) return 'bye';

  // 2. Un tabellone puo' esistere prima delle parti (una fase generata, i vincitori non
  //    ancora noti). Uno slot vuoto qui non e' un difetto: e' un incontro non ancora
  //    composto, e mostrarlo come «in attesa di referto» sarebbe falso.
  if (match.players.a === null || match.players.b === null) return 'incomplete';

  // 3. Nessun referto: si gioca (o si aspetta).
  if (match.result === null) return 'pending';

  // 4. 🔴 UN REFERTO **PROPOSTO** NON E' UN INCONTRO CONCLUSO, e questo ramo viene prima degli
  //    altri due apposta. Fino alla conferma del secondo giocatore il match resta `pending` lato
  //    server: qui `result` non e' nullo, quindi senza questo ramo l'incontro cadrebbe su
  //    `reported`/`decided` e la schermata direbbe «concluso» su una partita che nessuno ha
  //    ancora confermato — con il vincitore gia' dichiarato, se il motore lo mandasse. E' la
  //    bugia peggiore che questa sezione possa raccontare: afferma un esito al posto del server.
  //    ⚠️ Si guarda il `resultType`, non `match.status`: il campo che porta il fatto e' quello.
  if (match.result.resultType === LIMULO_RESULT_PROPOSED) return 'proposed';

  // 5. 🔴 IL REFERTO E' CONTESTATO, E L'INCONTRO E' FERMO. Questo ramo mancava, e la sua
  //    assenza non produceva nessun errore: una disputa scivolava fino al ramo 6 e usciva
  //    come `reported` — «c'e' un referto ma nessun vincitore dichiarato», che e' vero e
  //    **non e' il fatto**. Il fatto e' che i due referti non coincidono, che l'incontro non
  //    si chiude da solo e che a deciderlo sara' lo staff: senza questa riga la schermata lo
  //    taceva, e chi ha contestato non aveva modo di sapere se la sua contestazione fosse
  //    arrivata. ⚠️ Sta PRIMA del ramo 6 e DOPO `proposed`: una disputa e' un referto
  //    proposto che ha ricevuto risposta, non un caso del referto definitivo.
  if (match.result.resultType === LIMULO_RESULT_DISPUTED) return 'disputed';

  // 6. C'e' un referto definitivo. Il vincitore si dichiara, non si deduce.
  return winnerSideOf(match) === null ? 'reported' : 'decided';
}

/**
 * Chi sei tu, davanti a un referto **proposto**.
 *
 * 🔑 **Si DERIVA, non si aggiunge un campo** — ed e' la forma scelta da chi possiede il motore
 * `[T @gionko, kb#146 §1(i), 2026-09-08T15:29:10Z]`: *«`source` NON diventa la parte proponente:
 * resta `FONTI_REFERTO` (`'player'`); chi ha proposto si ricava da `canSubmitResult` (`true` per
 * chi deve confermare, `false` per chi ha proposto) piu' `isMe`»*.
 *
 * I tre esiti, e il quarto che conta piu' di tutti:
 *  - `proponent` — sono io e **non** posso piu' referare: la proposta e' mia, aspetto l'altro;
 *  - `confirmer` — sono io e posso ancora referare: tocca a me confermare o smentire;
 *  - `observer` — nessuno dei due lati sono io (staff, spettatore): guardo e basta;
 *  - 🔴 `unknown` — **`isMe` non e' arrivato**. Non e' un riempitivo ed e' il motivo per cui
 *    questa funzione non ritorna un booleano: senza `isMe` il ruolo **non e' derivabile**, e
 *    l'unica risposta onesta e' «non lo so». Chi la riceve mostra il referto proposto e
 *    **nessun bottone che afferma**: un'attribuzione inventata farebbe premere «Confermo» a
 *    chi ha appena proposto — cioe' farebbe chiudere l'incontro a una parte sola, che e'
 *    esattamente il difetto che la conferma esiste per togliere.
 *
 * ⚠️ `canSubmitResult` da solo NON basta a dire «ho proposto io»: e' `false` anche per uno
 * spettatore, per un incontro concluso e per chi non ha diritto di refertare. Il lato lo dice
 * `isMe`, il turno lo dice `canSubmitResult`: servono entrambi, e servono in quest'ordine.
 */
export type LimuloProposalRole = 'proponent' | 'confirmer' | 'observer' | 'unknown';

export function proposalRoleOf(match: LimuloMatch): LimuloProposalRole {
  const a = match.players.a?.isMe;
  const b = match.players.b?.isMe;

  // Nessuno dei due lati porta il campo: il ruolo non e' derivabile. (Un solo lato che lo
  // porta basta: `isMe: false` su A e assente su B dice comunque che A non sono io, ma non
  // dice che B lo sia — quindi il caso «uno solo, ed e' false» resta `unknown` per B.)
  if (a === undefined && b === undefined) return 'unknown';

  const sonoIo = a === true || b === true;
  if (!sonoIo) {
    // ⚠️ «Nessun `true`» significa spettatore SOLO se entrambi i lati hanno parlato. Se uno
    // dei due tace, il mio lato potrebbe essere quello muto: dire `observer` sarebbe
    // affermare un'assenza sulla base di un dato mancante.
    return a === undefined || b === undefined ? 'unknown' : 'observer';
  }

  return match.canSubmitResult ? 'confirmer' : 'proponent';
}

/**
 * Chi ha fatto il check-in, per lato. `checkins` porta l'id della PARTE, come `playerA`/`playerB`.
 *
 * 🔴 **Una voce di `checkins` NON significa «ha fatto il check-in».** `LimuloMatchCheckin` ha
 * un campo `checkedIn` booleano (`types/limulo.ts:267`): esiste la riga che dice **di no** —
 * un check-in revocato, o registrato e non confermato. Un `Set` degli id presenti la
 * conterebbe come un sì, e sarebbe un'affermazione **rovesciata** ricavata dal dato giusto.
 *
 * ⚠️ E l'assenza resta ambigua nell'altro verso: un elenco vuoto significa «nessuno risulta»,
 * non «nessuno ha fatto il check-in» — la facciata puo' semplicemente non portare il dato.
 * Per questo la funzione risponde solo alla domanda positiva («risulta presente?») e chi la
 * usa non deve rendere `false` come «assente»: al massimo come «non risulta».
 */
export function checkedInSides(match: LimuloMatch): { a: boolean; b: boolean } {
  const presenti = new Set(match.checkins.filter((c) => c.checkedIn).map((c) => c.playerId));
  return { a: presenti.has(match.playerA), b: presenti.has(match.playerB) };
}

/**
 * Il nome da mostrare per una parte, o `null` se il server non ne ha dato uno.
 *
 * ⚠️ **`null`, non stringa vuota.** Una stringa vuota si renderizza come uno spazio bianco
 * dove il lettore si aspetta un nome, e a schermo e' indistinguibile da un caricamento. Con
 * `null` chi chiama e' costretto a scegliere la propria copia («avversario da definire»,
 * «partecipante»), che e' una decisione di prodotto e non deve stare qui.
 *
 * L'ordine e' quello gia' usato per le righe di classifica (`model.ts:259-262`): il nome
 * scelto dall'utente prima dello username tecnico.
 */
export function participantNameOf(
  participant: { displayName?: string; username?: string } | null | undefined,
): string | null {
  if (!participant) return null;
  const nome = participant.displayName?.trim() || participant.username?.trim() || '';
  return nome === '' ? null : nome;
}

/**
 * Perche' la lista degli incontri e' vuota — con i dati che ABBIAMO, e senza inventare.
 *
 * 🔴 IL FATTO CHE RENDE NECESSARIA QUESTA FUNZIONE [M 2026-09-06, `qTournaments@origin/main`
 * `superficie/capability/incontri.ts:365-383`]: **`GET /v1/tournaments/:id/matches` non
 * restituisce il tabellone, restituisce i TUOI incontri.** Il resolver filtra sulle parti
 * dell'utente (`find(scoped(org, {tournamentId, 'members.user': utente}))` → `$or` su
 * `home`/`away`), e il BFF mette SEMPRE l'utente nel contesto
 * (`buildTournamentMatchPayload` allega `context.userId` a ogni richiesta,
 * `router.js:360`). Per un non iscritto la risposta e' `items: []` **per disegno**, e il
 * commento del provider lo dice: «la busta vuota, che e' la risposta giusta e non un
 * ripiego: la domanda era "i miei incontri", e i suoi sono nessuno».
 *
 * ⇒ La copy che diceva «il tabellone di questo torneo non e' ancora stato generato» era
 * **falsa per uno spettatore su un torneo con il tabellone gia' pronto** — cioe' il vuoto
 * affermato che questa sezione esiste per non produrre. Misurato su «Ciao Cup»: 2 incontri
 * su Mongo, un iscritto ne vede 1, un non iscritto 0.
 *
 * ⚠️ E il terzo verdetto NON e' un riempitivo. Se lo stato d'iscrizione non e' arrivato,
 * la risposta e' `unknown` — non «non sei iscritto», che sarebbe un'affermazione fatta al
 * posto del server sulla base di un dato che non abbiamo. E' la stessa regola di
 * `eligibility.ts`: «non lo so» e' un verdetto, e va detto.
 */
export type LimuloEmptyMatchesReason = 'not-registered' | 'registered-none' | 'unknown';

export function emptyMatchesReasonOf(input: {
  /** `true` se la query dello stato d'iscrizione ha fallito. */
  registrationFailed: boolean;
  /** Lo stato d'iscrizione gia' spacchettato; `null`/`undefined` = non e' arrivato. */
  registration: { registered: boolean } | null | undefined;
}): LimuloEmptyMatchesReason {
  // ⚠️ L'ERRORE VIENE PRIMA DEL DATO, e non e' pedanteria: su una query fallita RTK Query
  // conserva l'ULTIMO `data` buono accanto al nuovo `error`. Leggere il dato per primo
  // significherebbe dichiarare un'iscrizione sulla fede di una risposta vecchia — lo stesso
  // difetto gia' pagato sulla rilettura del referto (`matchActions.ts`, `outcomeData`).
  if (input.registrationFailed) return 'unknown';
  if (input.registration === null || input.registration === undefined) return 'unknown';
  return input.registration.registered ? 'registered-none' : 'not-registered';
}

/**
 * Il referto di CHI HA CONTESTATO, estratto da `result.reason`.
 *
 * 🔑 **Perche' da una stringa e non da un campo.** I numeri del contestatore stanno in
 * `disputedScoreA/B` sul motore, ma il sanitizer del BFF (`policy.js`, letterale a chiavi
 * fisse) **scarta ogni chiave nuova a livello referto**: al client non arrivano. Per farli
 * passare comunque il motore li scrive anche in `reason`, che e' l'unico campo di testo
 * libero che il sanitizer lascia passare, nella forma `disputa:<parte>:<home>-<away>`
 * `[T @gionko, kb#146 §5, 2026-09-09T23:04:25Z]`.
 *
 * **Confermato da chi possiede il motore, letto sul sorgente di `1.24.0`/`af156a0`**
 * `[T @gionko, kb#146, 2026-09-10T00:15:25Z]`: forma costruita con `String(n)` sui due numeri
 * (`incontri.ts`, `propostaAnnidataDi`); `<parte>` e' l'id di parte (`pty-` + 8 esadecimali),
 * **lo stesso valore di `playerA`/`playerB`**; punteggio sempre dal lato `playerA`, come
 * `scoreA/scoreB`; e i negativi escono davvero come `2--1` — o `-1-2` se e' il primo — perche'
 * il dominio non legifera ne' sul segno ne' sugli interi (A5, ADR-0063).
 *
 * ⚠️ **Confermato non e' esercitato**: nessuna disputa vera e' ancora passata di qui (serve un
 * incontro `pending` con due parti, che su staging non esiste senza un atto di conduzione).
 * ⇒ la funzione resta a rispondere `null` a ogni scostamento invece di indovinare, e chi la
 * chiama deve poter mostrare «c'e' una disputa, decide lo staff» **anche senza i numeri**: una
 * disputa senza cifre e' una schermata povera, una con cifre inventate e' una schermata falsa.
 *
 * ⚠️ `side` segue la stessa regola di `winnerSideOf`: e' `null` quando la parte nominata non
 * e' nessuna delle due di questo incontro. Nessun «se non e' A allora e' B».
 */
export interface LimuloDisputedScore {
  /** L'id di PARTE dichiarato dal motore come autore della contestazione. */
  party: string;
  /** `'a'`/`'b'` solo se quella parte e' una delle due di questo incontro. */
  side: 'a' | 'b' | null;
  home: number;
  away: number;
}

export function disputedScoreOf(match: LimuloMatch): LimuloDisputedScore | null {
  const reason = match.result?.reason;
  if (typeof reason !== 'string') return null;

  // `split` con un limite darebbe una terza parte che si mangia i due punti successivi:
  // qui si pretende ESATTAMENTE tre parti, cosi' una forma diversa da quella dichiarata
  // cade su `null` invece di essere interpretata a meta'.
  const parti = reason.split(':');
  if (parti.length !== 3 || parti[0] !== 'disputa') return null;

  const party = parti[1];
  if (party === '') return null;

  // ⚠️ Il separatore dei due punteggi e' un trattino, e i punteggi possono essere NEGATIVI o
  // frazionari (ADR-0063/A5: «il dominio dichiara di non sapere come conta un gioco»). Un
  // `split('-')` ingenuo spezzerebbe `2--1` nel posto sbagliato e ne ricaverebbe due numeri
  // plausibili e falsi: il gruppo del secondo numero deve quindi ammettere il proprio segno.
  const m = /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/.exec(parti[2]);
  if (m === null) return null;

  const home = Number(m[1]);
  const away = Number(m[2]);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return null;

  const side = party === match.playerA ? 'a' : party === match.playerB ? 'b' : null;
  return { party, side, home, away };
}

/**
 * L'istante in cui il silenzio-assenso scade, o `null` se non lo sappiamo.
 *
 * 🔴 **LA DOMANDA A CUI SERVE, e che il client non sapeva porsi.** Scaduti i 15 minuti
 * **anche chi ha proposto** torna `canSubmitResult: true`, e la sua ripetizione identica
 * chiude l'incontro `[T @gionko, kb#146 §6]`. Da quell'istante i due lati sono
 * **indistinguibili** con i campi che arrivano: `proposalRoleOf` risponde `confirmer` a
 * entrambi. Il comportamento resta giusto — rimandare lo stesso punteggio e' esattamente
 * cio' che serve — ma la copy della conferma («tocca a te confermare», «se non fai nulla
 * entro 15 minuti vale questo») davanti al proponente **e' falsa due volte**: attribuisce
 * all'altro un referto che e' suo, e promette una scadenza gia' passata.
 *
 * ⇒ La cura NON e' indovinare chi ha proposto (con questi dati non e' derivabile): e'
 * **smettere di attribuire il referto a qualcuno** quando la finestra e' chiusa. Una frase
 * vera per entrambi i lati batte una frase precisa che per meta' e' sbagliata.
 *
 * ⚠️ `null` quando `recordedAt` non c'e': «non lo so» resta un verdetto, e chi chiama
 * mostra la copy neutra invece di dedurre una scadenza da un dato che non ha.
 *
 * ═══ 🔑 LA PORTATA, fissata il 2026-09-20 (tma-orc-10-1-sviluppo, dopo la reference I-101) ═══
 * La reference del referto vieta di **simulare la scadenza da `recordedAt`**. Il divieto ha un
 * oggetto solo: **mostrare un NUMERO**. Nessun conto alla rovescia, nessun «scade fra N
 * minuti» — quella e' una precisione che non abbiamo. Usare la deduzione per **scegliere fra
 * due copy** e' un atto diverso, e toglierla rimetterebbe il difetto che curava: il proponente
 * a finestra chiusa che legge «tocca a te confermare», falso due volte.
 *
 * 🔴 **E LA DEDUZIONE DEVE FALLIRE NEL VERSO CHE NON CHIUDE UNA PORTA.**
 *  · sbaglia dicendo «non ancora scaduto» → offre «Confermo» → decide il server: nessun danno;
 *  · sbaglia dicendo «scaduto» → offre «Chiudi con questo referto» → l'utente chiude un
 *    incontro che avrebbe potuto contestare: danno vero e **irreversibile**.
 * ⇒ nel dubbio si resta su «non ancora scaduto», e questo `null` **non** conta come scaduto.
 * Il cancello lo asserisce su entrambi i lati (`tools/verify-limulo.ts`, §20).
 *
 * 📌 ~~IL GIORNO CHE `expiresAt` ATTRAVERSERA' IL BORDO, la deduzione si sostituisce QUI.~~ **E' arrivato
 * il 01/10** (#477 parte C): `result.expiresAt` lo scrive il motore CON la proroga di un torneo in pausa
 * (`scadenzaDellaProposta`, qTournaments `incontri.js:313`) e il BFF lo passa da qlash-limulo#181
 * (`tournament-fields.js:132` @ `f0119c7`, servito da `staging-f0119c7`). ⇒ **Vince il server**, anche
 * quando dice «gia' passata» e la deduzione direbbe «c'e' ancora tempo»: la proroga la conosce solo lui,
 * e nell'altro verso (deduzione scaduta, server no) la deduzione dopo una pausa diceva «i 15 minuti sono
 * passati» con la finestra ancora aperta — il difetto C di #477.
 * La deduzione da `recordedAt` resta SOLO come ripiego: `expiresAt` assente (un BFF di prima, un referto
 * in disputa) o illeggibile. Il divieto sul NUMERO a schermo resta com'e': anche col dato vero, questa
 * funzione sceglie fra due copy e non diventa un conto alla rovescia (non e' stato deciso).
 */
export function proposalDeadlineAtOf(match: LimuloMatch): string | null {
  const dalServer = match.result?.expiresAt;
  if (typeof dalServer === 'string' && dalServer !== '') {
    const s = new Date(dalServer).getTime();
    if (Number.isFinite(s)) return new Date(s).toISOString();
  }
  const recordedAt = match.result?.recordedAt;
  if (typeof recordedAt !== 'string' || recordedAt === '') return null;
  const t = new Date(recordedAt).getTime();
  if (!Number.isFinite(t)) return null;
  return new Date(t + LIMULO_PROPOSAL_WINDOW_MS).toISOString();
}

// ═════════════════════════════════════════════════════════════════════════════
// I TRE CASI DEL NOME (reference D2 e lista iscritti, approvate il 2026-09-20)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * ⚠️ **`'Player'` E' UN VALORE RISERVATO DI FATTO, e il perche' e' quello che rende sicuro
 * il confronto su una stringa.** Il bordo ripiega su questo letterale quando c'e' una persona
 * **senza** nickname valido, e `NICKNAME_PATTERN` e' `/^[a-z0-9_]{3,32}$/` — **solo
 * minuscole** ⇒ `Player` con la P maiuscola **non puo' mai essere il nickname di qualcuno**.
 * Non e' una collisione improbabile: e' una collisione **impossibile**.
 */
export const LIMULO_NICKNAME_FALLBACK = 'Player';

/**
 * Chi c'e' dietro una parte. **Tre casi, e sono tre cose diverse.**
 *
 *  · `nickname`      — c'e' una persona e ha scelto un nome: si mostra quello;
 *  · `senza-nickname`— c'e' una **persona**, e non ha un nickname valido (il bordo ha
 *                      riscritto il nome nel suo ripiego);
 *  · `senza-persona` — 🔴 **non c'e' nessuno**: parte di sola entita', che il bordo **non
 *                      riscrive affatto** ⇒ `displayName` e' quello del motore e puo' essere
 *                      un identificativo qualsiasi;
 *  · `assente`       — la parte non e' arrivata (un lato non ancora composto).
 *
 * 🔴 **ERRATA CHE VALE PIU' DELLA REGOLA, e sta qui perche' e' il difetto che questa
 * funzione esiste per evitare.** La prima stesura della reference usava **due** casi —
 * *«`username` vuoto ⇒ giocatore senza nickname»* — ed e' **giusta sulle righe che il bordo
 * riscrive e falsa su quelle che non tocca**: una parte senza membri ha `userId: null` ⇒
 * nessuna riscrittura ⇒ `username` vuoto **senza** che compaia `Player`. Quella riga veniva
 * chiamata «giocatore senza nickname», che non e' un'approssimazione: e' **un'altra cosa**.
 * ⭐ E il motore lo dichiarava gia', nel commento accanto al campo: *«`null` quando la parte
 * non ha membri: non e' `''`, perche' "non c'e' una persona" e "c'e' una persona senza id"
 * sono due cose diverse»*.
 *
 * ⚠️ **L'ORDINE DEI RAMI E' LA LOGICA.** Il ripiego del bordo si riconosce per primo, perche'
 * su quelle righe `username` puo' essere vuoto **e** `displayName` essere `'Player'`: testare
 * prima `username` le manderebbe nel terzo caso, cioe' direbbe «posto senza giocatore» di una
 * persona vera.
 */
export type LimuloNomeCaso = 'nickname' | 'senza-nickname' | 'senza-persona' | 'assente';

export function participantNameCaseOf(
  participant: { displayName?: string; username?: string } | null | undefined,
): LimuloNomeCaso {
  if (!participant) return 'assente';
  const displayName = participant.displayName?.trim() ?? '';
  const username = participant.username?.trim() ?? '';
  if (displayName === LIMULO_NICKNAME_FALLBACK) return 'senza-nickname';
  if (username !== '') return 'nickname';
  // ⚠️ Niente `username`, e il bordo **non** ha messo il suo ripiego ⇒ non c'e' stata nessuna
  // riscrittura ⇒ non c'e' una persona. Vale anche quando `displayName` e' pieno: quel valore
  // viene dal motore e puo' essere un identificativo, non un nome da mostrare.
  return 'senza-persona';
}

/**
 * Le chiavi di copy dei tre casi che NON sono un nickname. Stanno qui, accanto al criterio che
 * le sceglie, e non nei componenti: il caso e la sua frase sono la stessa decisione.
 */
export const LIMULO_NOME_COPY: Readonly<Record<Exclude<LimuloNomeCaso, 'nickname'>, string>> =
  Object.freeze({
    'senza-nickname': 'matches.noNickname',
    'senza-persona': 'matches.noPlayer',
    assente: 'matches.unknownPlayer',
  });

/** Come si scrive il nome di una parte: il testo, e se va smorzato perche' NON e' un nome. */
export interface LimuloNomeEtichetta {
  testo: string;
  /**
   * ⚠️ Vero nei tre casi in cui il testo e' una NOSTRA frase e non un'etichetta scelta da
   * qualcuno. Chi rende una riga lo usa per il corsivo; chi lo infila dentro una frase lo
   * ignora, e va bene: la distinzione tipografica non sopravvive dentro «X ha dichiarato 2-1».
   */
  smorzato: boolean;
}

/**
 * 🔑 **IL MODO DI SCRIVERE UN NOME, IN UN POSTO SOLO** — reference lista iscritti e D2
 * (2026-09-20): *«il riverbero non e' solo questa schermata»*. `router.js:288` passa **ogni**
 * capability `tournament.*` per `withPublicParticipantNames`, quindi i tre casi valgono
 * identici sulla lista iscritti, sui **due lati di un incontro** (`players.a/b`), nelle frasi
 * di **conferma e disputa** del referto, in classifica e sulla busta di stato.
 *
 * 🔴 **E IL DIFETTO CHE QUESTA FUNZIONE CHIUDE ERA MISURABILE SULLO STESSO SCHERMO** [M
 * 2026-09-20 22:3xZ, `origin/main` = `4ee0aff`]: l'intestazione del dettaglio incontro
 * scriveva «Giocatore senza nickname» (tre casi) e il pannello di conferma tre centimetri
 * sotto scriveva «**Player** 2 - 1 marta_rx» (due casi, `participantNameOf(...) ?? unknown`).
 * Due frasi sullo stesso giocatore, nella stessa schermata, in disaccordo — e «Posto senza
 * giocatore» non compariva **mai** dentro una frase del referto.
 *
 * ⚠️ **`t` arriva come PARAMETRO**, come in `lib/limulo/auth.ts` e `matchActions.ts`: questo
 * livello resta puro e `tools/verify-limulo.ts` puo' esercitarlo sotto Node senza i18n.
 * ⛔ E non si ripiega su una stringa vuota: un nome che manca e' uno dei tre casi, sempre uno
 * dei tre, e un vuoto a schermo e' indistinguibile da un caricamento.
 */
export function participantLabelOf(
  participant: { displayName?: string; username?: string } | null | undefined,
  t: (key: string) => string,
): LimuloNomeEtichetta {
  const caso = participantNameCaseOf(participant);
  if (caso === 'nickname') {
    // ⚠️ Il `?? ''` non e' raggiungibile — `nickname` implica un nome non vuoto — ma un tipo
    // `string | null` non lo sa, e un `!` qui sarebbe una promessa senza cancello.
    return { testo: participantNameOf(participant) ?? '', smorzato: false };
  }
  return { testo: t(LIMULO_NOME_COPY[caso]), smorzato: true };
}

// ═════════════════════════════════════════════════════════════════════════════
// I QUATTRO ESITI DEL REFERTO (reference E2+E3, I-101, approvata il 2026-09-20)
// ═════════════════════════════════════════════════════════════════════════════

/**
 * Quale dei banner del referto va mostrato, o `null` se non ce n'e' nessuno.
 *
 * 🔑 **QUATTRO esiti nella reference, CINQUE qui, e il quinto non e' un'aggiunta mia**:
 * `scaduto` e' il ramo che `#216` ha introdotto e che la reference **non disegna** perche' le
 * sue quattro immagini non lo mostrano. Fonderlo in `tocca-a-te` rimetterebbe il difetto che
 * quella PR curava: passati i 15 minuti `canSubmitResult` torna `true` **su entrambi i lati**
 * `[T @gionko, kb#146 §6]`, quindi il titolo «X ha dichiarato 2-1» attribuirebbe all'avversario
 * un referto che puo' essere il tuo. ⇒ lo scaduto ha un banner che **non nomina nessuno**.
 */
export type LimuloRefertoBanner =
  | 'tocca-a-te'
  | 'in-attesa'
  | 'scaduto'
  | 'disputa'
  | 'non-lo-so'
  /** kb#303 §3 (#474): il referto aspetta, ma il torneo non e' in corso — non e' un «non so chi sei». */
  | 'torneo-fermo'
  | 'torneo-terminato';

/**
 * ⚠️ **`assensoScaduto` arriva come PARAMETRO e non si ricalcola qui.** La scadenza viene da
 * `proposalDeadlineAtOf` (il `result.expiresAt` del server, o il ripiego `recordedAt` +
 * `LIMULO_PROPOSAL_WINDOW_MS`) ed e' confrontata con l'ora corrente da un hook:
 * rifarla in questa funzione sarebbe una **seconda sorgente** dello stesso fatto, cioe' due
 * verita' che divergono il giorno che una delle due cambia. Qui resta puro cio' che e' puro.
 *
 * ⚠️ **L'ORDINE DEI RAMI E' LA LOGICA**, ed e' lo stesso che il pannello aveva prima di avere
 * i banner — questa funzione **non cambia il comportamento, gli da' un nome**:
 *  1. senza referto, o con un `resultType` che non e' ne' proposto ne' conteso, non c'e' banner;
 *  2. la **disputa** batte tutto: li' non c'e' un'attesa di conferma, c'e' un disaccordo;
 *  3. la **scadenza** viene PRIMA del ruolo (v. sopra, ed e' la correzione di `#216`);
 *  4. poi il ruolo: `confirmer` -> tocca a te, `proponent` -> in attesa;
 *  5. `unknown`/`observer` -> «non lo so», che ⛔ **non e' una cortesia**: offrire «Confermo» a
 *     chi potrebbe essere l'altro giocatore chiuderebbe un incontro con l'assenso della persona
 *     sbagliata.
 */
export function refertoBannerOf(
  match: LimuloMatch,
  /**
   * `torneo`: a torneo NON in corso il ruolo non si deduce (la 1.83 spegne `canSubmitResult` per entrambi i
   * lati), e il banner lo dice come torneo fermo o terminato — non come «non riusciamo a dire quale lato
   * sei», che e' un altro fatto (revisione 2 di #474, difetto 2-bis).
   */
  opts: { assensoScaduto: boolean; torneo?: { inCorso: boolean | null; terminato: boolean } },
): LimuloRefertoBanner | null {
  const tipo = match.result?.resultType;
  if (tipo === LIMULO_RESULT_DISPUTED) return 'disputa';
  if (tipo !== LIMULO_RESULT_PROPOSED) return null;
  if (opts.torneo?.inCorso === false) return opts.torneo.terminato ? 'torneo-terminato' : 'torneo-fermo';
  if (opts.assensoScaduto) return 'scaduto';
  const ruolo = proposalRoleOf(match);
  if (ruolo === 'confirmer') return 'tocca-a-te';
  if (ruolo === 'proponent') return 'in-attesa';
  return 'non-lo-so';
}

/**
 * Chi ha proposto il referto — ed e' una **DEDUZIONE, non un campo**.
 *
 * 🔴 `proposedBy` **non attraversa il sanitizer** (cercato uno per uno): la parte che ha
 * proposto e' quella che **non puo' piu' refertare**, cioe' l'altra rispetto a chi sta
 * confermando. Sta scritto qui, nel codice che la fa, perche' *«fra sei mesi qualcuno la
 * leggerebbe come un dato»* (reference E2+E3).
 *
 * ⛔ **`null` fuori dalla finestra dei 15 minuti e quando il ruolo non e' derivabile**, e non e'
 * prudenza: passata la finestra i due lati sono **indistinguibili** (`proposalRoleOf` risponde
 * `confirmer` a entrambi), quindi qualunque nome qui sarebbe tirato a sorte fra due.
 */
export function proposerSideOf(
  match: LimuloMatch,
  opts: { assensoScaduto: boolean },
): 'a' | 'b' | null {
  if (opts.assensoScaduto) return null;
  if (proposalRoleOf(match) !== 'confirmer') return null;
  // Chi conferma e' `isMe`; chi ha proposto e' l'altro lato. Se `isMe` non e' su nessuno dei
  // due, `proposalRoleOf` non avrebbe risposto `confirmer`.
  if (match.players.a?.isMe === true) return 'b';
  if (match.players.b?.isMe === true) return 'a';
  return null;
}
